import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

/**
 * Numbered step flow — short ordered items rendered as a horizontal
 * row of cards on wide screens, vertical on narrow.
 *
 * Routes from an ordered list whose items are all short (<= ~80 chars)
 * and whose count is between 2 and 6.
 */
export type StepFlowItem = {
  body: ReactNode;
  /** Optional eyebrow / sub-label above the body. */
  label?: ReactNode;
};

export type StepFlowProps = {
  items: StepFlowItem[];
  className?: string;
};

export function StepFlow({ items, className }: StepFlowProps) {
  return (
    <ol
      className={cn(
        "my-6 grid gap-3",
        items.length === 2
          ? "sm:grid-cols-2"
          : items.length === 3
            ? "sm:grid-cols-3"
            : items.length === 4
              ? "sm:grid-cols-2 md:grid-cols-4"
              : "sm:grid-cols-2 md:grid-cols-3",
        className,
      )}
    >
      {items.map((item, idx) => (
        <li
          key={idx}
          className={cn(
            "rounded-2xl bg-stone-200 px-4 py-4 sm:px-5 sm:py-5",
            "flex flex-col gap-3 min-h-[84px]",
          )}
        >
          <span
            className="text-[11px] font-semibold uppercase tracking-[0.18em] tabular-nums text-stone-500"
            dir="ltr"
          >
            {String(idx + 1).padStart(2, "0")}
          </span>
          <div className="text-[14px] leading-snug text-stone-800">
            {item.label ? (
              <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-stone-500 mb-1">
                {item.label}
              </p>
            ) : null}
            {item.body}
          </div>
        </li>
      ))}
    </ol>
  );
}
